import React, { useMemo, useCallback } from "react";
import { Button, Space } from "antd";
import { UploadOutlined, InboxOutlined, CheckCircleOutlined, InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { FormField } from "@cloudscape-design/components";
import { useController, Control } from "react-hook-form";
import { FormField as FormFieldType } from "../../../../types/formSchema";
import { MockDataManager } from "../../../../utils/mockDataManager";
import { useFormAPI } from "../../../../hooks/useFormAPI";
import "./upload-field.scss";
import { logger } from '../../../../utils/logger';

interface FieldComponentProps {
  field: FormFieldType;
  control: Control<any>;
  onFieldChange?: (fieldName: string, value: any) => void;
  workflowId: string; // Workflow identifier for API calls and state management
}

// Custom validation rule converter
const convertValidationRules = (field: FormFieldType) => {
  const rules: any = {};

  if (field.validation) {
    field.validation.forEach((rule) => {
      if (rule.required) {
        rules.required = rule.message || `${field.label} is required`;
      }
      if (rule.min) {
        rules.minLength = {
          value: rule.min,
          message: rule.message || `Minimum length is ${rule.min}`,
        };
      }
      if (rule.max) {
        rules.maxLength = {
          value: rule.max,
          message: rule.message || `Maximum length is ${rule.max}`,
        };
      }
      if (rule.pattern) {
        rules.pattern = {
          value: new RegExp(rule.pattern),
          message: rule.message || "Invalid format",
        };
      }
    });
  }

  return rules;
};

// Upload Field Component - Native HTML Input Implementation
export const UploadFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
  workflowId,
}) => {
  // Use API hook for making API calls
  const { triggerAPICall } = useFormAPI(workflowId);
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: {
      ...convertValidationRules(field),
      validate: (value) => {
        if (!value?.files || value.files.length === 0) {
          return field.validation?.find(v => v.required)?.message || 'File is required';
        }
        
        const fileErrors = value.files.filter((file: any) => file.status === 'error');
        if (fileErrors.length > 0) {
          return fileErrors[0].error;
        }
        
        return true;
      }
    },
  });

  const uploadField = field as any;
  const config = uploadField.uploadConfig || {};

  // Generate unique input ID for this field instance
  const inputId = useMemo(() => `upload-input-${field.name}-${Date.now()}`, [field.name]);

  // Generate proper accept attribute for file dialog filtering
  const acceptAttribute = useMemo(() => {
    const acceptedTypes = config.accept || ['.csv'];
    return acceptedTypes.join(',');
  }, [config.accept]);

  // Handle native file input change and trigger API analysis
  const handleFileInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const processedFiles = fileArray.map((file, index) => ({
        uid: `${Date.now()}-${index}`,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'done',
        originFileObj: file,
        lastModified: file.lastModified,
      }));

      const fileValue = {
        files: processedFiles,
        uploadStatus: 'success'
      };
      
      controllerField.onChange(fileValue);
      onFieldChange?.(field.name, fileValue);

      // Trigger mock API analysis for column mapping
      if (config.triggerAnalysis !== false) {
        try {
          await triggerAPICall(
            `${field.name}_analysis`,
            () => MockDataManager.simulateAPICall('rvm_column_analysis_mock', 2000)
          );
        } catch (error) {
          logger.error('File analysis failed:', error as Error);
          // API error is already stored in form store by triggerAPICall
        }
      }
    }
  }, [controllerField, onFieldChange, field.name, config.triggerAnalysis]);

  // Handle file removal
  const handleFileRemove = useCallback((fileToRemove: any) => {
    const currentFiles = controllerField.value?.files || [];
    const updatedFiles = currentFiles.filter((file: any) => file.uid !== fileToRemove.uid);
    
    const fileValue = {
      files: updatedFiles,
      uploadStatus: 'success'
    };
    
    controllerField.onChange(fileValue);
    onFieldChange?.(field.name, fileValue);
    
    // Reset the file input
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }, [controllerField, onFieldChange, field.name, inputId]);

  // Handle click on upload area
  const handleUploadAreaClick = useCallback(() => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  }, [inputId]);

  // Fields Information Section Component
  const FieldsInformationSection = () => {
    if (uploadField.showFieldsInfo === false) return null;

    const requiredFields = uploadField.requiredFields || [];
    const optionalFields = uploadField.optionalFields || [];

    if (requiredFields.length === 0 && optionalFields.length === 0) return null;

    return (
      <div className="finspace-upload-field__fields-info">
        {requiredFields.length > 0 && (
          <div className="finspace-upload-field__fields-section">
            <div className="finspace-upload-field__fields-title finspace-upload-field__fields-title--required">
              Required Fields
            </div>
            <Space direction="vertical" size="small">
              {requiredFields.map((fieldText: string, index: number) => (
                <div key={index} className="finspace-upload-field__field-item">
                  <CheckCircleOutlined className="finspace-upload-field__field-icon finspace-upload-field__field-icon--required" />
                  <span className="finspace-upload-field__field-text">{fieldText}</span>
                </div>
              ))}
            </Space>
          </div>
        )}
        
        {optionalFields.length > 0 && (
          <div className="finspace-upload-field__fields-section">
            <div className="finspace-upload-field__fields-title finspace-upload-field__fields-title--optional">
              Optional Fields
            </div>
            <Space direction="vertical" size="small">
              {optionalFields.map((fieldText: string, index: number) => (
                <div key={index} className="finspace-upload-field__field-item">
                  <InfoCircleOutlined className="finspace-upload-field__field-icon finspace-upload-field__field-icon--optional" />
                  <span className="finspace-upload-field__field-text">{fieldText}</span>
                </div>
              ))}
            </Space>
          </div>
        )}
      </div>
    );
  };

  const currentFiles = controllerField.value?.files || [];

  return (
    <FormField
      label={field.label}
      description={field.tooltip}
      errorText={error?.message}
    >
      <FieldsInformationSection />
      
      {/* Native HTML file input (hidden) */}
      <input
        id={inputId}
        type="file"
        accept={acceptAttribute}
        multiple={config.multiple || false}
        className="finspace-upload-field__input"
        onChange={handleFileInputChange}
      />
      
      {/* Custom styled upload area */}
      <div
        onClick={handleUploadAreaClick}
        className={`finspace-upload-field__area ${error ? 'finspace-upload-field__area--error' : ''}`}
      >
        <div className="finspace-upload-field__icon-container">
          <InboxOutlined className="finspace-upload-field__icon" />
        </div>
        <div className="finspace-upload-field__primary-text">
          {config.dragDropText || 'Choose files or drag and drop'}
        </div>
        <div className="finspace-upload-field__secondary-text">
          Supported formats: {config.supportedFormats?.join(', ') || 'CSV'}
        </div>
        <div className="finspace-upload-field__secondary-text">
          Maximum file size: {config.maxSizeDisplay || '1 GB'}
        </div>
        <Button 
          icon={<UploadOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleUploadAreaClick();
          }}
          className="finspace-upload-field__button"
        >
          Choose files
        </Button>
      </div>

      {/* Display selected files */}
      {currentFiles.length > 0 && (
        <div className="finspace-upload-field__files-container">
          {/* Selected files header */}
          <div className="finspace-upload-field__files-header">
            Selected files
          </div>
          
          {currentFiles.map((file: any) => (
            <div
              key={file.uid}
              className="finspace-upload-field__file-item"
            >
              <div className="finspace-upload-field__file-content">
                <CheckCircleOutlined className="finspace-upload-field__file-status-icon" />
                <div className="finspace-upload-field__file-details">
                  <div className="finspace-upload-field__file-name">
                    {file.name}
                  </div>
                  <div className="finspace-upload-field__file-size">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                className="finspace-upload-field__remove-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileRemove(file);
                }}
              />
            </div>
          ))}
        </div>
      )}
    </FormField>
  );
};

export default UploadFieldComponent;
